import { Close } from "@material-ui/icons";
import {
  Box,
  Card,
  ClickAwayListener,
  Divider,
  Grid,
  Grow,
  IconButton,
  InputAdornment,
  Paper,
  Popover,
  styled,
  TextField,
} from "@mui/material";
import { DateRangeIcon } from "@mui/x-date-pickers";
import {
  DateCalendar,
  FormLabel,
  PrimaryButton,
  SecondaryButton,
} from "components";
import moment from "moment-timezone";
import { useRef, useState } from "react";
import { formatPeriodDate } from "utils";

const DateRangePicker = ({
  label,
  startDate,
  endDate,
  handleChangeStartDate,
  handleChangeEndDate,
  disabled,
  clear,
  width,
  placeholder,
  startMinDate,
  startMaxDate,
  endMinDate,
  endMaxDate,
  useReset = false,
}) => {
  const inputRef = useRef(null);
  const emptyDate = !startDate && !endDate;
  const [anchorEl, setAnchorEl] = useState(null);
  const [period, setPeriod] = useState({
    start: startDate,
    end: endDate,
  });

  const togglePopper = event => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    if (anchorEl) {
      handleReset();
    }
  };
  const handleClickAway = () => {
    setAnchorEl(null);
    if (anchorEl) {
      handleReset();
    }
  };
  const handleClear = () => {
    handleChangeStartDate(null);
    handleChangeEndDate(null);
  };
  const handleReset = () => {
    handleChangePeriod(startDate, "start");
    handleChangePeriod(endDate, "end");
  };
  const handleChangePeriod = (value, key) =>
    setPeriod(state => ({
      ...state,
      [key]: value,
    }));
  const onChangeStartDate = date => {
    const startPeriod = moment(date);
    handleChangePeriod(startPeriod, "start");
  };

  const onChangeEndDate = date => {
    const endPeriod = moment(date);
    handleChangePeriod(endPeriod, "end");
  };

  const handleSubmit = () => {
    handleChangeStartDate(period?.start);
    handleChangeEndDate(period?.end);
    setAnchorEl(null);
  };

  return (
    <Box>
      <CustomTextField
        ref={inputRef}
        customWidth={width}
        size="small"
        label={label}
        placeholder={placeholder}
        value={emptyDate ? "" : formatPeriodDate(startDate, endDate)}
        onClick={togglePopper}
        InputProps={{
          readOnly: true,
          shrink: !emptyDate,
          fullWidth: true,
          endAdornment: (
            <InputAdornment position="end">
              {clear && (startDate || endDate) && (
                <IconButton onClick={handleClear}>
                  <Close />
                </IconButton>
              )}
              <IconButton onClick={togglePopper}>
                <DateRangeIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
        disabled={disabled}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClickAway}
        anchorOrigin={{
          vertical: "bottom",
        }}
        transformOrigin={{
          vertical: "top",
        }}
        TransitionComponent={Grow}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper
            elevation={3}
            sx={{
              padding: 2,
            }}
          >
            <Grid container spacing={3} direction="row" wrap="nowrap">
              <Grid item>
                <FormLabel label="Start Date" />
                <Card>
                  <DateCalendar
                    value={period?.start}
                    onChange={onChangeStartDate}
                    minDate={startMinDate}
                    maxDate={startMaxDate}
                  />
                </Card>
              </Grid>
              <Grid item>
                <FormLabel label="End Date" />
                <Card>
                  <DateCalendar
                    value={period?.end}
                    onChange={onChangeEndDate}
                    minDate={endMinDate}
                    maxDate={endMaxDate}
                  />
                </Card>
              </Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Grid container justifyContent={useReset ? "space-between" : "flex-end"} alignItems="center" wrap="nowrap">
              {useReset && (
                <Grid item>
                  <SecondaryButton
                    size="small"
                    onClick={() => {
                      handleChangePeriod(null, "start");
                      handleChangePeriod(null, "end");
                      handleChangeStartDate(null);
                      handleChangeEndDate(null);
                      setAnchorEl(null);
                    }}
                    label="Reset"
                  />
                </Grid>
              )}
              <Grid item>
                <Grid container spacing={1} wrap="nowrap">
                  <Grid item>
                    <SecondaryButton
                      size="small"
                      onClick={togglePopper}
                      label="Cancel"
                    />
                  </Grid>
                  <Grid item>
                    <PrimaryButton
                      size="small"
                      onClick={handleSubmit}
                      label="Apply"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </ClickAwayListener>
      </Popover>
    </Box>
  );
};

const CustomTextField = styled(TextField)(({ customWidth }) => ({
  width: customWidth || 300,
}));
export default DateRangePicker;
